/**
 * Shared narrowing helpers for errors thrown by the generated OpenAPI client.
 *
 * The mutation `onError` callbacks in this folder are typed
 * `Error | { body?: { message?: string } }`, so reading `.body` / `.status`
 * directly fails on the `Error` arm (TS2339). These helpers narrow first and
 * only then read, without changing any user-visible message.
 */

/** The shape the generated client's `ApiError` carries in addition to `Error`. */
export interface ApiErrorLike {
  body?: {
    message?: unknown;
    error?: unknown;
    errors?: unknown;
  };
  status?: number;
}

/**
 * Narrows an unknown mutation error to the generated client's ApiError-ish shape.
 *
 * This proves key PRESENCE only — it says nothing about the *value* shape of
 * `body.message`. Always read through `apiErrorMessage` / `apiErrorDetail`
 * rather than touching `body.message` directly, so a non-string wire payload
 * (e.g. `{ message: { de: '…' } }`) falls through to the caller's literal
 * fallback instead of rendering `[object Object]` into a toast.
 */
export function isApiErrorLike(error: unknown): error is ApiErrorLike {
  return (
    typeof error === 'object' &&
    error !== null &&
    ('body' in error || 'status' in error)
  );
}

/** `body.message`, but only when it really is a non-empty string. */
export function apiErrorMessage(error: unknown): string | undefined {
  if (!isApiErrorLike(error)) return undefined;
  const message = error.body?.message;
  return typeof message === 'string' && message.length > 0 ? message : undefined;
}

/** `body.error`, but only when it really is a non-empty string. */
export function apiErrorDetail(error: unknown): string | undefined {
  if (!isApiErrorLike(error)) return undefined;
  const detail = error.body?.error;
  return typeof detail === 'string' && detail.length > 0 ? detail : undefined;
}

/** `body.errors`, the field-keyed validation map, when it is an object. */
export function apiValidationErrors(
  error: unknown,
): Record<string, unknown> | undefined {
  if (!isApiErrorLike(error)) return undefined;
  const errors = error.body?.errors;
  return typeof errors === 'object' && errors !== null
    ? (errors as Record<string, unknown>)
    : undefined;
}

/** The HTTP status carried by the client error, when present. */
export function apiErrorStatus(error: unknown): number | undefined {
  if (!isApiErrorLike(error)) return undefined;
  return typeof error.status === 'number' ? error.status : undefined;
}

/**
 * The error's own `message`, but only when it really is a non-empty string.
 *
 * Accepts any object carrying a `message` (not just `Error` instances) because
 * the pre-existing call sites read `error.message` off the declared
 * `Error | { body?: … }` union — a rejected plain `{ message: '…' }` must keep
 * reaching the toast exactly as it did before this narrowing fix.
 */
export function nativeErrorMessage(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' && message.length > 0 ? message : undefined;
}
